/**
 * Azure Blob Storage Service
 * Uploads files directly to Azure Blob Storage using SAS token
 */

// Azure Blob Storage configuration
const AZURE_CONTAINER_URL = process.env.REACT_APP_AZURE_STORAGE_CONTAINER || 'https://fanclash.blob.core.windows.net/motorsport-news';
const AZURE_SAS_TOKEN = process.env.REACT_APP_AZURE_SAS_TOKEN || 'sv=2021-10-04&spr=https%2Chttp&st=2026-01-26T15%3A48%3A29Z&se=2031-01-31T15%3A48%3A00Z&sr=c&sp=cwl&sig=9WlKfH7%2BNEFSSPW05rKJRgCFfkuqr6DGadAEbZ%2BpR5Y%3D';

/**
 * Destination container base URL (for public access)
 */
export const DEST_CONTAINER_URL = AZURE_CONTAINER_URL;

/**
 * Get the full URL with SAS token for uploading
 */
const getUploadUrl = (fileName) => {
  return `${AZURE_CONTAINER_URL}/${fileName}?${AZURE_SAS_TOKEN}`;
};

/**
 * Upload a single blob to Azure
 * @param {ArrayBuffer} data - The file data to upload
 * @param {string} fileName - Destination filename
 * @param {string} contentType - MIME type
 */
const uploadBlob = async (data, fileName, contentType) => {
  const uploadUrl = getUploadUrl(fileName);
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-ms-blob-type': 'BlockBlob',
    },
    body: data,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure upload failed (${response.status}): ${errorText}`);
  }

  return `${AZURE_CONTAINER_URL}/${fileName}`;
};

/**
 * Upload an image file directly to Azure Blob Storage
 * Uploads both -medium.jpg and -small.jpg versions (same image for now)
 * Used for drag-and-drop image uploads from user's computer
 * @param {File} file - The image file to upload
 * @param {string} guid - The GUID to use for naming the file
 * @returns {Object} - Object with destination URLs
 */
export const uploadImageFile = async (file, guid) => {
  if (!file || !guid) {
    throw new Error('Missing file or guid');
  }

  if (!AZURE_SAS_TOKEN) {
    throw new Error('Azure SAS token not configured. Please check REACT_APP_AZURE_SAS_TOKEN in .env');
  }

  console.log('Uploading image file to Azure:', { fileName: file.name, guid, size: file.size });

  const mediumFileName = `${guid}-medium.jpg`;
  const smallFileName = `${guid}-small.jpg`;
  const contentType = file.type || 'image/jpeg';

  try {
    // Read file as ArrayBuffer for upload
    const arrayBuffer = await file.arrayBuffer();

    // Upload both versions in parallel (same image for both sizes)
    const [mediumUrl, smallUrl] = await Promise.all([
      uploadBlob(arrayBuffer, mediumFileName, contentType),
      uploadBlob(arrayBuffer, smallFileName, contentType),
    ]);

    console.log('Azure upload complete:', { mediumUrl, smallUrl });

    return {
      success: true,
      mediumUrl,
      smallUrl,
      guid,
      originalFileName: file.name,
      fileSize: file.size,
    };
  } catch (error) {
    console.error('Azure upload error:', error);
    throw new Error(`Image upload failed: ${error.message}`);
  }
};

/**
 * Copy a blob from source URL to destination container
 * Uses fetch to download and re-upload (since cross-origin copy may not work)
 * @param {string} sourceUrl - Source URL of the blob
 * @param {string} guid - GUID for naming
 * @param {string} mediaType - Type of media (image-large, image-small, video, video-thumbnail)
 */
const copyBlob = async (sourceUrl, guid, mediaType) => {
  if (!sourceUrl || !guid) {
    throw new Error('Missing sourceUrl or guid');
  }

  if (!AZURE_SAS_TOKEN) {
    throw new Error('Azure SAS token not configured. Please check REACT_APP_AZURE_SAS_TOKEN in .env');
  }

  console.log('Copying blob to Azure:', { sourceUrl, guid, mediaType });

  // Determine destination filename based on mediaType
  let destFileName;
  let contentType;
  switch (mediaType) {
    case 'image-large':
      destFileName = `${guid}-medium.jpg`;
      contentType = 'image/jpeg';
      break;
    case 'image-small':
      destFileName = `${guid}-small.jpg`;
      contentType = 'image/jpeg';
      break;
    case 'video':
      destFileName = `${guid}-video.mp4`;
      contentType = 'video/mp4';
      break;
    case 'video-thumbnail':
      destFileName = `${guid}-video-image.jpg`;
      contentType = 'image/jpeg';
      break;
    default:
      destFileName = `${guid}-medium.jpg`;
      contentType = 'image/jpeg';
  }

  try {
    // Download the source file
    const sourceResponse = await fetch(sourceUrl);
    if (!sourceResponse.ok) {
      throw new Error(`Failed to fetch source: ${sourceResponse.status}`);
    }
    const blob = await sourceResponse.blob();

    // Upload to Azure
    const uploadUrl = getUploadUrl(destFileName);
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
        'x-ms-blob-type': 'BlockBlob',
      },
      body: blob,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Azure upload failed (${uploadResponse.status}): ${errorText}`);
    }

    const destinationUrl = `${AZURE_CONTAINER_URL}/${destFileName}`;

    return {
      success: true,
      destinationUrl,
      destFileName,
      mediaType,
      guid,
    };
  } catch (error) {
    console.error('Blob copy error:', error);
    throw new Error(`Blob copy failed: ${error.message}`);
  }
};

/**
 * Copy image files (small and large) to destination container
 * @param {Object} image - Image object with SmallImageUrl, LargeImageUrl, and GUID
 * @returns {Object} - Object with new URLs
 */
export const copyImageToDestination = async (image) => {
  const guid = image.GUID;
  const results = {
    smallImageUrl: null,
    largeImageUrl: null,
  };

  // Copy small image -> GUID-small.jpg
  if (image.SmallImageUrl) {
    const smallResult = await copyBlob(image.SmallImageUrl, guid, 'image-small');
    results.smallImageUrl = smallResult.destinationUrl;
  }

  // Copy large image -> GUID-medium.jpg
  if (image.LargeImageUrl) {
    const largeResult = await copyBlob(image.LargeImageUrl, guid, 'image-large');
    results.largeImageUrl = largeResult.destinationUrl;
  }

  return results;
};

/**
 * Copy video files (video and thumbnail) to destination container
 * @param {Object} video - Video object with VideoUrl, ImageUrl, and GUID
 * @returns {Object} - Object with new URLs
 */
export const copyVideoToDestination = async (video) => {
  const guid = video.GUID;
  const results = {
    videoUrl: null,
    thumbnailUrl: null,
  };

  // Copy video file -> GUID-video.mp4
  if (video.VideoUrl) {
    const videoResult = await copyBlob(video.VideoUrl, guid, 'video');
    results.videoUrl = videoResult.destinationUrl;
  }

  // Copy thumbnail image -> GUID-video-image.jpg
  if (video.ImageUrl) {
    const thumbResult = await copyBlob(video.ImageUrl, guid, 'video-thumbnail');
    results.thumbnailUrl = thumbResult.destinationUrl;
  }

  return results;
};

/**
 * Copy media (image or video) to destination container
 * @param {Object} media - Media object with mediaType property and GUID
 * @returns {Object} - Object with new URLs
 */
export const copyMediaToDestination = async (media) => {
  if (media.mediaType === 'video') {
    return copyVideoToDestination(media);
  } else {
    return copyImageToDestination(media);
  }
};
